import React from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
} from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Footer from '@/components/Footer';

const FONT = '"Inter", system-ui, -apple-system, sans-serif';
const ACCENT = '#10b981';
const ACCENT_DARK = '#059669';
const pill = { borderRadius: '999px', textTransform: 'none' as const, fontFamily: FONT, fontWeight: 600, px: 3.5 };

const AboutPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ bgcolor: 'white', fontFamily: FONT }}>
      {/* ─── Hero ───────────────────────────────────────────────── */}
      <Box sx={{ py: { xs: 10, md: 14 }, position: 'relative', overflow: 'hidden' }}>
        <Box sx={{
          position: 'absolute', inset: 0, zIndex: 0,
          background: `
            radial-gradient(ellipse 80% 60% at 50% 30%, rgba(16,185,129,0.06) 0%, transparent 60%),
            linear-gradient(to bottom, #fafbfc 0%, white 100%)
          `,
        }} />

        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <Typography
            variant="h1"
            sx={{
              fontFamily: FONT, fontWeight: 800,
              fontSize: { xs: '2.2rem', md: '3rem' },
              lineHeight: 1.1, letterSpacing: '-0.03em',
              color: '#0f172a', mb: 2,
            }}
          >
            About us
          </Typography>
          <Typography sx={{
            fontFamily: FONT, fontSize: { xs: '1.05rem', md: '1.2rem' },
            color: 'text.secondary', lineHeight: 1.6, maxWidth: 560, mx: 'auto',
          }}>
            We're on a mission to elevate the youth sports experience for families everywhere.
          </Typography>
        </Container>
      </Box>

      {/* ─── Content ────────────────────────────────────────────── */}
      <Container maxWidth="md" sx={{ pb: { xs: 10, md: 14 } }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {/* Mission */}
          <Box>
            <Typography sx={{
              fontFamily: FONT, fontWeight: 800, letterSpacing: '-0.02em',
              fontSize: { xs: '1.5rem', md: '1.8rem' }, color: '#0f172a', mb: 2,
            }}>
              Our mission
            </Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: '1.05rem', color: 'text.secondary', lineHeight: 1.8 }}>
              Youth sports should be about the kids. The teamwork, the growth, the unforgettable moments on the field.
              But for families juggling multiple players across multiple leagues, it often feels more like logistics than fun.
              Scattered schedules, surprise conflicts, and last-minute scrambles for directions can turn a Saturday into a source
              of stress instead of joy.
            </Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: '1.05rem', color: 'text.secondary', lineHeight: 1.8, mt: 2 }}>
              Player Central exists to change that. We believe every family deserves a simple, reliable way to stay organized
              so they can spend less time managing schedules and more time cheering from the sideline.
            </Typography>
          </Box>

          {/* Story */}
          <Box>
            <Typography sx={{
              fontFamily: FONT, fontWeight: 800, letterSpacing: '-0.02em',
              fontSize: { xs: '1.5rem', md: '1.8rem' }, color: '#0f172a', mb: 2,
            }}>
              Why we built this
            </Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: '1.05rem', color: 'text.secondary', lineHeight: 1.8 }}>
              Player Central was born out of firsthand experience. As a family with kids in competitive sports, we lived the
              chaos: checking three different league websites for schedules, realizing too late that two games overlapped,
              and scrambling to figure out which field was which. We built the tool we wished existed. One place to see
              everything, with smart features that help you plan ahead instead of react.
            </Typography>
          </Box>

          {/* Company */}
          <Box>
            <Typography sx={{
              fontFamily: FONT, fontWeight: 800, letterSpacing: '-0.02em',
              fontSize: { xs: '1.5rem', md: '1.8rem' }, color: '#0f172a', mb: 2,
            }}>
              Eleventh Triangular
            </Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: '1.05rem', color: 'text.secondary', lineHeight: 1.8 }}>
              Eleventh Triangular is a small team passionate about making youth sports better for families. We're building
              thoughtfully, listening to the people who use it, and shipping features that make a real difference in
              day-to-day sports life.
            </Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: '1.05rem', color: 'text.secondary', lineHeight: 1.8, mt: 2 }}>
              The name? In footy, positions 1 through 11 sum to 66, the eleventh triangular number.
            </Typography>
          </Box>
        </Box>
      </Container>

      {/* ─── CTA ────────────────────────────────────────────────── */}
      <Box sx={{ py: { xs: 10, md: 14 }, bgcolor: '#0f172a' }}>
        <Container maxWidth="sm" sx={{ textAlign: 'center' }}>
          <Typography sx={{
            fontFamily: FONT, fontWeight: 800, letterSpacing: '-0.02em',
            fontSize: { xs: '1.8rem', md: '2.4rem' }, color: 'white', mb: 2,
          }}>
            Want to get in touch?
          </Typography>
          <Typography sx={{
            fontFamily: FONT, fontSize: '1.1rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.6, mb: 5,
          }}>
            We'd love to hear from you. Questions, feedback, or just to say&nbsp;hi.
          </Typography>
          <Button
            size="large"
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            sx={{
              ...pill, bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_DARK },
              boxShadow: `0 4px 14px ${ACCENT}40`, py: 1.5, px: 4, fontSize: '1rem',
            }}
            onClick={() => navigate('/contact')}
          >
            Contact us
          </Button>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
};

export default AboutPage;
